import { EContentService } from "../../../globals/services/enhancedContent.service";
import { UsersService } from "../../../globals/services/users.service";
import { ReactComponent as TemplateIcon } from '../../../assets/images/template.svg';
import { ReactComponent as CopyIcon } from '../../../assets/images/copyClipboard.svg';
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { PAGE_SIZE } from "../constants/index";
import ListFilter from "../components/ListFilter";
import AdvancedFiltersDropdown from "../components/AdvancedFiltersDropdown";
import ListTabel from "../components/ListTabel";
const moment = require("moment").default || require("moment");

export const TemplateList = (props) => {
    const { onCountUpdate, currentPage, setCurrentPage, navigateToTemplatePage } = props;
    const eContentService = EContentService();
    const usersService = UsersService();
    const [list, setList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [volumeList, setVolumeList] = useState([]);
    const [nameFilter, setNameFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [volumeFilter, setVolumeFilter] = useState("All");
    const [languageFilter, setLanguageFilter] = useState("All");
    const [createdDateFilter, setCreatedDateFilter] = useState("All");
    const [createdByFilter, setCreatedByFilter] = useState("All");
    const [lastModificationDateFilter, setLastModificationDateFilter] = useState("All");
    const [noFoldersFilter, setNoFoldersFilter] = useState("All");
    const [prevFilters, setPrevFilters] = useState({
        name: "",
        status: "All",
        volume: "All",
        language: "All",
        createdDate: "All",
        createdBy: "All",
        lastModificationDate: "All",
        noFolders: "All"
    });
    const [isFiltersDropdownOpen, setIsFiltersDropdownOpen] = useState(false);
    const [availableCreatedBy, setAvailableCreatedBy] = useState([]);
    const [userIdToNameMap, setUserIdToNameMap] = useState({});
    const { me } = useSelector((state) => state.auth);

    // Extract unique languages from templates
    const availableLanguages = useMemo(() => {
        const languages = list
            .map(template => template.language)
            .filter(language => language && language.trim() !== "")
            .filter((language, index, arr) => arr.indexOf(language) === index)
            .sort();
        return languages;
    }, [list]);

    // Extract unique created dates from templates
    const availableCreatedDates = useMemo(() => {
        const dates = list
            .map(template => template.created)
            .filter(date => date && date.trim() !== "")
            .map(date => moment(date).format("MMMM D, YYYY"))
            .filter((date, index, arr) => arr.indexOf(date) === index)
            .sort((a, b) => moment(b, "MMMM D, YYYY").diff(moment(a, "MMMM D, YYYY")));
        return dates;
    }, [list]);

    // Extract unique last modification dates from templates
    const availableLastModificationDates = useMemo(() => {
        const dates = list
            .map(template => template.modified)
            .filter(date => date && date.trim() !== "")
            .map(date => moment(date).format("MMMM D, YYYY"))
            .filter((date, index, arr) => arr.indexOf(date) === index)
            .sort((a, b) => moment(b, "MMMM D, YYYY").diff(moment(a, "MMMM D, YYYY")));
        return dates;
    }, [list]);

    // Create folder count options based on actual data for No. Folders filter
    const availableNoFolders = useMemo(() => {
        if (list.length === 0) return [];
        
        const folderCounts = list.map(template => 
            template.folderItems ? template.folderItems.length : 0
        );
        
        // Get unique folder counts that actually exist in the data
        const uniqueCounts = [...new Set(folderCounts)].sort((a, b) => a - b);
        
        // Convert to strings for consistency with other filters
        return uniqueCounts.map(count => count.toString());
    }, [list]);

    // Extract unique user IDs from templates
    const uniqueUserIds = useMemo(() => {
        return list
            .map(template => template.createdBy)
            .filter(userId => userId && userId.trim() !== "")
            .filter((userId, index, arr) => arr.indexOf(userId) === index);
    }, [list]);

    // Fetch user data for created by filter
    useEffect(() => {
        if (uniqueUserIds.length > 0) {
            usersService.getUserByArrayOfIds(uniqueUserIds)
                .then(users => {
                    const nameMap = {};
                    const userNames = [];
                    
                    users.forEach(user => {
                        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user._id;
                        nameMap[user._id] = fullName;
                        userNames.push(fullName);
                    });
                    
                    setUserIdToNameMap(nameMap);
                    setAvailableCreatedBy(userNames.sort());
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                    setAvailableCreatedBy([]);
                    setUserIdToNameMap({});
                });
        } else {
            setAvailableCreatedBy([]);
            setUserIdToNameMap({});
        }
    }, [uniqueUserIds]);

    const applyFilters = useCallback(() => {
        return list.filter(template => {
            const matchesName = nameFilter ? template.title.toLowerCase().includes(nameFilter.toLowerCase()) : true;
            const matchesStatus = statusFilter === "All" || template.status === statusFilter;
            const matchesVolume = volumeFilter === "All" || template.volume === volumeFilter;
            const matchesLanguage = languageFilter === "All" || template.language === languageFilter;
            
            // Created date filter logic
            let matchesCreatedDate = true;
            if (createdDateFilter !== "All" && template.created) {
                const templateDate = moment(template.created).format("MMMM D, YYYY");
                matchesCreatedDate = templateDate === createdDateFilter;
            }
            
            // Created by filter logic - match selected user name to template's createdBy ID
            let matchesCreatedBy = true;
            if (createdByFilter !== "All" && createdByFilter) {
                // Find the user ID that corresponds to the selected name
                const selectedUserId = Object.keys(userIdToNameMap).find(
                    userId => userIdToNameMap[userId] === createdByFilter
                );
                matchesCreatedBy = template.createdBy === selectedUserId;
            }

            // Last modification date filter logic
            let matchesLastModificationDate = true;
            if (lastModificationDateFilter !== "All" && template.modified) {
                const templateModifiedDate = moment(template.modified).format("MMMM D, YYYY");
                matchesLastModificationDate = templateModifiedDate === lastModificationDateFilter;
            }
            
            // No. Folders filter logic
            let matchesNoFolders = true;
            if (noFoldersFilter !== "All") {
                const folderCount = template.folderItems ? template.folderItems.length : 0;
                matchesNoFolders = folderCount.toString() === noFoldersFilter;
            }

            return matchesName && matchesStatus && matchesVolume && matchesLanguage && matchesCreatedDate && matchesCreatedBy && matchesLastModificationDate && matchesNoFolders;
        });
    }, [list, nameFilter, statusFilter, volumeFilter, languageFilter, createdDateFilter, createdByFilter, lastModificationDateFilter, noFoldersFilter, userIdToNameMap]);

    useEffect(() => {
        const filtered = applyFilters();
        setFilteredList(filtered);
        onCountUpdate(filtered.length);

        const currentFilters = {
            name: nameFilter,
            status: statusFilter,
            volume: volumeFilter,
            language: languageFilter,
            createdDate: createdDateFilter,
            createdBy: createdByFilter,
            lastModificationDate: lastModificationDateFilter,
            noFolders: noFoldersFilter
        };

        // Check if any filter has changed
        const filtersChanged = Object.keys(currentFilters).some(key =>
            currentFilters[key] !== prevFilters[key]
        );

        if (filtersChanged) {
            setCurrentPage(1);
            setPrevFilters(currentFilters);
        }
    }, [list, nameFilter, statusFilter, volumeFilter, languageFilter, createdDateFilter, createdByFilter, lastModificationDateFilter, noFoldersFilter, userIdToNameMap, applyFilters, onCountUpdate, setCurrentPage, prevFilters]);

    const getCurrentPageItems = () => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return filteredList.slice(startIndex, endIndex);
    };

    const transformedItems = useMemo(() => {
        const currentPageItems = getCurrentPageItems();
        return currentPageItems.map(item => {
            const volumeObj = volumeList.find(vol => vol._id === item.volume);
            const volumeName = volumeObj ? volumeObj.title : (item.volume || 'N/A');
            
            return {
                ...item,
                title: item.title,
                volume: volumeName,
                status: item.status,
                language: item.language || 'N/A',
                createdBy: userIdToNameMap[item.createdBy] || item.createdBy || 'Unknown',
                lastModification: item.modified ? moment(item.modified).format("MMM D, YYYY") : 'N/A'
            };
        });
    }, [filteredList, currentPage, userIdToNameMap, volumeList]);

    // Define columns for the table
    const columns = [
        { key: 'title', label: 'Title' },
        { key: 'volume', label: 'Volume' },
        { key: 'status', label: 'Status' },
        { key: 'language', label: 'Language' },
        { key: 'createdBy', label: 'Created by' },
        { key: 'lastModification', label: 'Last Modification' }
    ];

    useEffect(() => {
        if (me) {
            eContentService.getTemplateList().then((res) => {
                setList(res.data);
            });
            eContentService.getActiveVolumeList().then((res) => {
                setVolumeList(res.data);
            });
        }
    }, [me]);

    const handleEdit = (item) => {
        navigateToTemplatePage(item);
    };

    // const handleDeleteTemplate = async (templateId) => {
    //     try {
    //         await eContentService.deleteTemplate(templateId);
    //         eContentService.getTemplateList().then((res) => {
    //             setList(res.data);
    //         });
    //     } catch (error) {
    //         console.error("Error deleting template:", error);
    //     }
    // };

    const handleUpdateTemplateStatus = async (templateId, status) => {
        const updatedTemplate = filteredList.find(template => template._id === templateId);
        if (!updatedTemplate) {
            console.error("Template not found");
            return;
        }

        try {
            await eContentService.updateTemplate({
                ...updatedTemplate,
                status,
                modifiedBy: me ? me._id : null,
                modified: moment().format('YYYY-MM-DD')
            });
            eContentService.getTemplateList().then((res) => {
                setList(res.data);
            });
        } catch (error) {
            console.error("Error updating template status:", error);
        }
    };

    const clearFilters = () => {
        setNameFilter("");
        setStatusFilter("All");
        setVolumeFilter("All");
        setLanguageFilter("All");
        setCreatedDateFilter("All");
        setCreatedByFilter("All");
        setLastModificationDateFilter("All");
        setNoFoldersFilter("All");
    };

    const clearAdvancedFilters = () => {
        setLanguageFilter("All");
        setCreatedDateFilter("All");
        setCreatedByFilter("All");
        setLastModificationDateFilter("All");
        setNoFoldersFilter("All");
    };

    const handleOpenFiltersDropdown = () => {
        setIsFiltersDropdownOpen(true);
    };

    const handleCloseFiltersDropdown = () => {
        setIsFiltersDropdownOpen(false);
    };

    return (
        <div>
            <ListFilter
                filterByName={setNameFilter}
                filterByStatus={setStatusFilter}
                filterByVolume={setVolumeFilter}
                nameFilter={nameFilter}
                statusFilter={statusFilter}
                volumeFilter={volumeFilter}
                volumeList={volumeList}
                clearFilters={clearFilters}
                hasAdvancedFilters={true}
                onOpenFiltersModal={handleOpenFiltersDropdown}
                languageFilter={languageFilter}
                createdDateFilter={createdDateFilter}
                createdByFilter={createdByFilter}
                lastModificationDateFilter={lastModificationDateFilter}
                noFoldersFilter={noFoldersFilter}
                advancedFiltersComponent={
                    <AdvancedFiltersDropdown
                        isOpen={isFiltersDropdownOpen}
                        onClose={handleCloseFiltersDropdown}
                        languageFilter={languageFilter}
                        onLanguageChange={setLanguageFilter}
                        createdDateFilter={createdDateFilter}
                        onCreatedDateChange={setCreatedDateFilter}
                        createdByFilter={createdByFilter}
                        onCreatedByChange={setCreatedByFilter}
                        lastModificationDateFilter={lastModificationDateFilter}
                        onLastModificationDateChange={setLastModificationDateFilter}
                        noFoldersFilter={noFoldersFilter}
                        onNoFoldersChange={setNoFoldersFilter}
                        availableLanguages={availableLanguages}
                        availableCreatedDates={availableCreatedDates}
                        availableCreatedBy={availableCreatedBy}
                        availableLastModificationDates={availableLastModificationDates}
                        availableNoFolders={availableNoFolders}
                        onClearAdvancedFilters={clearAdvancedFilters}
                    />
                }
            />

            <ListTabel
                itemName="templates"
                columns={columns}
                filteredItems={transformedItems}
                navigateToItemPage={handleEdit}
                // handleDeleteItem={handleDeleteTemplate}
                handleUpdateItemStatus={handleUpdateTemplateStatus}
            />
        </div>
    )
}